/**
 * v-scrollbars
 * Detects whether an element currently overflows vertically and/or horizontally.
 * Adds classes + data attributes and can invoke a callback.
 *
 * Default classes: has-v-scroll / has-h-scroll
 * Default data attributes: data-has-v-scroll / data-has-h-scroll ("true" | "false")
 *
 * Usage (default):
 *   <div v-scrollbars class="panel">...</div>
 *
 * Usage (custom):
 *   <div v-scrollbars="{ verticalClass: 'y', horizontalClass: 'x', onChange: s => console.log(s) }" />
 */
import type { Directive } from 'vue';

interface ScrollbarState {
    vertical: boolean; // element overflows vertically
    horizontal: boolean; // element overflows horizontally
    vAtStart: boolean; // at/near top
    vAtEnd: boolean; // at/near bottom
    hAtStart: boolean; // at/near left
    hAtEnd: boolean; // at/near right
}
interface ScrollbarOptions {
    verticalClass?: string;
    horizontalClass?: string;
    setAttributes?: boolean; // default true
    onChange?: (state: ScrollbarState, el: HTMLElement) => void;
    horizontalWheel?: boolean;
}

type Opts = Required<Omit<ScrollbarOptions, 'onChange'>> & { onChange?: ScrollbarOptions['onChange'] };

const DEFAULTS: Opts = {
    verticalClass: 'has-v-scroll',
    horizontalClass: 'has-h-scroll',
    setAttributes: true,
    onChange: undefined,
    horizontalWheel: false
};

function compute(el: HTMLElement): ScrollbarState {
    const tol = 0.5; // tolerance to reduce flicker from fractional pixels
    const vOverflow = el.scrollHeight - el.clientHeight > tol;
    const hOverflow = el.scrollWidth - el.clientWidth > tol;
    let vAtStart = false,
        vAtEnd = false,
        hAtStart = false,
        hAtEnd = false;
    if (vOverflow) {
        const maxY = el.scrollHeight - el.clientHeight;
        const y = el.scrollTop;
        vAtStart = y <= tol;
        vAtEnd = y >= maxY - tol;
    }
    if (hOverflow) {
        const maxX = el.scrollWidth - el.clientWidth;
        const x = el.scrollLeft;
        hAtStart = x <= tol;
        hAtEnd = x >= maxX - tol;
    }
    return { vertical: vOverflow, horizontal: hOverflow, vAtStart, vAtEnd, hAtStart, hAtEnd };
}

interface TrackedEl extends HTMLElement {
    __sb_prev__?: ScrollbarState;
    __sb_opts__?: Opts;
    __sb_cleanup__?: () => void;
    __sb_pending__?: number;
    __sb_wheel_attached__?: boolean;
    __sb_onWheel__?: (e: WheelEvent) => void;
}

function applyState(el: TrackedEl, state: ScrollbarState) {
    const { verticalClass, horizontalClass, setAttributes, onChange, horizontalWheel } = el.__sb_opts__ || DEFAULTS;
    const prev = el.__sb_prev__;

    const changed = (key: keyof ScrollbarState) => !prev || prev[key] !== state[key];

    // Overflow presence classes
    if (changed('vertical')) {
        el.classList.toggle(verticalClass, state.vertical);
        if (setAttributes) el.dataset.hasVScroll = String(state.vertical);
        if (!state.vertical) {
            el.classList.remove('at-y-start', 'at-y-end');
            if (setAttributes) {
                delete el.dataset.atYStart;
                delete el.dataset.atYEnd;
            }
        }
    }
    if (changed('horizontal')) {
        el.classList.toggle(horizontalClass, state.horizontal);
        if (setAttributes) el.dataset.hasHScroll = String(state.horizontal);
        if (!state.horizontal) {
            el.classList.remove('at-x-start', 'at-x-end');
            if (setAttributes) {
                delete el.dataset.atXStart;
                delete el.dataset.atXEnd;
            }
        }
    }

    // Edge classes (only if overflow)
    if (state.vertical) {
        if (changed('vAtStart')) {
            el.classList.toggle('at-y-start', state.vAtStart);
            if (setAttributes) el.dataset.atYStart = String(state.vAtStart);
        }
        if (changed('vAtEnd')) {
            el.classList.toggle('at-y-end', state.vAtEnd);
            if (setAttributes) el.dataset.atYEnd = String(state.vAtEnd);
        }
    }
    if (state.horizontal) {
        if (changed('hAtStart')) {
            el.classList.toggle('at-x-start', state.hAtStart);
            if (setAttributes) el.dataset.atXStart = String(state.hAtStart);
        }
        if (changed('hAtEnd')) {
            el.classList.toggle('at-x-end', state.hAtEnd);
            if (setAttributes) el.dataset.atXEnd = String(state.hAtEnd);
        }
    }

    // Attach/detach scroll wheel listener based on overflow and option
    if (horizontalWheel && state.horizontal) {
        if (!el.__sb_wheel_attached__) {
            el.__sb_onWheel__ = (e: WheelEvent) => {
                // Only handle horizontal scrolling if there is overflow
                if (el.scrollWidth <= el.clientWidth) return;

                const atStart = el.scrollLeft <= 0;
                const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1; // -1 for tolerance

                // If at start and scrolling up (left), let event bubble
                if (atStart && e.deltaY <= 0) return;
                // If at end and scrolling down (right), let event bubble
                if (atEnd && e.deltaY >= 0) return;

                // Otherwise, handle the scroll and prevent default
                e.preventDefault();
                el.scrollLeft += e.deltaY;
            };
            el.addEventListener('wheel', el.__sb_onWheel__!, { passive: false });
            el.__sb_wheel_attached__ = true;
        }
    } else {
        if (el.__sb_wheel_attached__) {
            el.removeEventListener('wheel', el.__sb_onWheel__!);
            el.__sb_wheel_attached__ = false;
            el.__sb_onWheel__ = undefined;
        }
    }

    if (
        !prev ||
        prev.vertical !== state.vertical ||
        prev.horizontal !== state.horizontal ||
        prev.vAtStart !== state.vAtStart ||
        prev.vAtEnd !== state.vAtEnd ||
        prev.hAtStart !== state.hAtStart ||
        prev.hAtEnd !== state.hAtEnd
    ) {
        onChange?.(state, el);
    }

    el.__sb_prev__ = state;
}

function schedule(el: TrackedEl) {
    if (el.__sb_pending__ != null) return;
    el.__sb_pending__ = requestAnimationFrame(() => {
        el.__sb_pending__ = undefined;
        applyState(el, compute(el));
    });
}

function init(el: TrackedEl, value?: ScrollbarOptions) {
    el.__sb_cleanup__?.();

    el.__sb_opts__ = { ...DEFAULTS, ...value };

    const resizeObs = 'ResizeObserver' in window ? new ResizeObserver(() => schedule(el)) : undefined;
    resizeObs?.observe(el);

    const mutationObs = 'MutationObserver' in window ? new MutationObserver(() => schedule(el)) : undefined;
    mutationObs?.observe(el, { childList: true, subtree: true, characterData: true });

    const onScroll = () => schedule(el);
    el.addEventListener('scroll', onScroll, { passive: true });

    const onWinResize = () => schedule(el);
    window.addEventListener('resize', onWinResize, { passive: true });

    el.__sb_cleanup__ = () => {
        resizeObs?.disconnect();
        mutationObs?.disconnect();
        el.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onWinResize);
        if (el.__sb_wheel_attached__ && el.__sb_onWheel__) {
            el.removeEventListener('wheel', el.__sb_onWheel__);
            el.__sb_wheel_attached__ = false;
            el.__sb_onWheel__ = undefined;
        }
        if (el.__sb_pending__ != null) cancelAnimationFrame(el.__sb_pending__);
    };

    applyState(el, compute(el));
    requestAnimationFrame(() => applyState(el, compute(el)));
}

const ScrollbarsDirective: Directive<HTMLElement, ScrollbarOptions | undefined> = {
    mounted(el, binding) {
        init(el as TrackedEl, binding.value);
    },
    updated(el, binding) {
        if (binding.oldValue !== binding.value) {
            init(el as TrackedEl, binding.value);
        } else {
            schedule(el as TrackedEl);
        }
    },
    unmounted(el) {
        (el as TrackedEl).__sb_cleanup__?.();
    }
};

export { ScrollbarsDirective };
